import { useEffect, useMemo, useRef, useState } from "react";
import type { HTMLInputTypeAttribute } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  AssignmentWorkflowStep,
  getAssignmentProgress,
  getAssignmentStampPaperPreview,
  saveAssignmentStampPaper,
  sendAssignmentStampPaperWhatsApp,
  type AssignmentStampPaperMessagePreview,
  type AssignmentStampPaperMessageType,
  type AssignmentStampPaperPreviewResponse,
  type SaveAssignmentStampPaperPayload,
} from "../../api/assignment";
import { getStampVendors, type StampVendorOption } from "../../api/discussion";

export const ASSIGNMENT_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID =
  "assignment-stamp-paper-details-to-vendor-form";

const APPLICANT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
];

const STAMP_PAPER_COUNT_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

type FieldErrors = {
  stampVendorName?: string;
  stampVendorPhone?: string;
  assignee?: string;
  assigneeAddress?: string;
  assigneeType?: string;
  assigneeSignatoryName?: string;
  assigneeSignatoryDesignation?: string;
};

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = APPLICANT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
}

function validateVendorPhone(phone: string): string | undefined {
  const trimmed = phone.trim();
  if (!trimmed) return "Enter the stamp vendor phone number";
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) {
    return "Enter a valid phone number";
  }
  return undefined;
}

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  type = "text",
  inputMode,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={{ ...LABEL_FEATURES, color: "#222222" }}>
        {label}
      </label>
      <div
        className={`${INPUT_BASE} ${
          errorText ? "border-red-400 focus-within:ring-red-400" : ""
        }`}
      >
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`min-w-0 flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}

function FetchedDetailsPanel({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full"
      style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              value ? "text-[#222222]" : "text-[#6D6D6D]"
            }`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function StampCountSelect({
  label,
  value,
  onChange,
  errorText,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
      </label>
      <Select
        value={String(value ?? 0)}
        onValueChange={(v) => {
          const parsed = parseInt(v, 10);
          onChange(Number.isFinite(parsed) ? parsed : 0);
        }}
      >
        <SelectTrigger
          className={`h-9 min-h-[44px] md:min-h-[36px] w-full border ${
            errorText ? "border-red-400" : "border-[#eee]"
          } rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]`}
        >
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {STAMP_PAPER_COUNT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ApplicantTypeSelect({
  label,
  value,
  onChange,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
      </label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger
          className={`h-9 min-h-[44px] md:min-h-[36px] w-full border ${
            errorText ? "border-red-400" : "border-[#eee]"
          } rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]`}
        >
          <SelectValue placeholder="Text Here" />
        </SelectTrigger>
        <SelectContent>
          {APPLICANT_TYPE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type AssignmentStampPaperDetailsToVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function AssignmentStampPaperDetailsToVendorView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: AssignmentStampPaperDetailsToVendorViewProps) {
  // Read-only applicant block fetched from application details
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState<string | null>(null);
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [tmNumbers, setTmNumbers] = useState<string>("");

  // Editable assignee block
  const [assignee, setAssignee] = useState("");
  const [assigneeAddress, setAssigneeAddress] = useState("");
  const [assigneeType, setAssigneeType] = useState("");
  const [assigneeSignatoryName, setAssigneeSignatoryName] = useState("");
  const [assigneeSignatoryDesignation, setAssigneeSignatoryDesignation] =
    useState("");

  // Editable stamp counts
  const [applicantPoaStamps, setApplicantPoaStamps] = useState(0);
  const [assigneePoaStamps, setAssigneePoaStamps] = useState(0);
  const [assignmentDeedStamps, setAssignmentDeedStamps] = useState(0);
  const [otherDocumentsStamps, setOtherDocumentsStamps] = useState(0);

  // Vendor
  const [stampVendorName, setStampVendorName] = useState("");
  const [stampVendorPhone, setStampVendorPhone] = useState("");
  const [stampVendors, setStampVendors] = useState<StampVendorOption[]>([]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [messages, setMessages] = useState<AssignmentStampPaperMessagePreview[]>(
    []
  );

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyPreview = (data: AssignmentStampPaperPreviewResponse) => {
    setApplicant(data.applicant ?? "");
    setApplicantAddress(data.applicantAddress ?? "");
    setApplicantType(data.applicantType ?? null);
    setSignatoryName(data.signatoryName ?? "");
    setSignatoryDesignation(data.signatoryDesignation ?? "");
    setTmNumbers(
      (data.applications ?? [])
        .map((app) => app.tmNo)
        .filter((tmNo) => typeof tmNo === "string" && tmNo.trim().length > 0)
        .join(", ")
    );
    setAssignee(data.assignee ?? "");
    setAssigneeAddress(data.assigneeAddress ?? "");
    setAssigneeType(data.assigneeType ?? "");
    setAssigneeSignatoryName(data.assigneeSignatoryName ?? "");
    setAssigneeSignatoryDesignation(data.assigneeSignatoryDesignation ?? "");
    setApplicantPoaStamps(data.applicantPoaStamps ?? 0);
    setAssigneePoaStamps(data.assigneePoaStamps ?? 0);
    setAssignmentDeedStamps(data.assignmentDeedStamps ?? 0);
    setOtherDocumentsStamps(data.otherDocumentsStamps ?? 0);
    setStampVendorName(data.stampVendorName ?? "");
    setStampVendorPhone(data.stampVendorPhone ?? "");
    if (Array.isArray(data.stampVendors) && data.stampVendors.length > 0) {
      setStampVendors(data.stampVendors);
    }
    setMessages(data.messages ?? []);
  };

  // Initial load: preview + saved stamp vendors list + workflow progress
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getAssignmentStampPaperPreview(leadId),
      getStampVendors().catch((): StampVendorOption[] => []),
    ])
      .then(([preview, vendors]) => {
        if (cancelled) return;
        applyPreview(preview);
        if (Array.isArray(vendors) && vendors.length > 0) {
          setStampVendors(vendors);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load stamp paper details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Workflow progress – is this step already completed (sent)?
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getAssignmentProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === AssignmentWorkflowStep.AS_S2_01_StampPaperDetailsToVendor
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  const buildPayload = (): SaveAssignmentStampPaperPayload => ({
    assignee: assignee.trim() || undefined,
    assigneeAddress: assigneeAddress.trim() || undefined,
    assigneeType: assigneeType || undefined,
    assigneeSignatoryName: assigneeSignatoryName.trim() || undefined,
    assigneeSignatoryDesignation:
      assigneeSignatoryDesignation.trim() || undefined,
    applicantPoaStamps,
    assigneePoaStamps,
    assignmentDeedStamps,
    otherDocumentsStamps,
    stampVendorName: stampVendorName.trim() || undefined,
    stampVendorPhone: stampVendorPhone.trim() || undefined,
  });

  // Debounced auto-save for any preview-affecting fields (stamp counts, vendor, assignee)
  // so the WhatsApp previews always reflect the latest saved state.
  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!leadId || loading) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveAssignmentStampPaper(leadId, buildPayload())
        .then((data) => {
          setMessages(data.messages ?? []);
        })
        .catch(() => {
          // Auto-save errors stay silent; explicit submit surfaces real issues.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leadId,
    applicantPoaStamps,
    assigneePoaStamps,
    assignmentDeedStamps,
    otherDocumentsStamps,
    assignee,
    assigneeAddress,
    assigneeType,
    assigneeSignatoryName,
    assigneeSignatoryDesignation,
    stampVendorName,
    stampVendorPhone,
  ]);

  const handleVendorSelect = (value: string) => {
    if (value === "__none__") {
      setStampVendorName("");
      setStampVendorPhone("");
      setFieldErrors((prev) => ({
        ...prev,
        stampVendorName: undefined,
        stampVendorPhone: undefined,
      }));
      return;
    }
    setStampVendorName(value);
    const vendor = stampVendors.find((v) => v.name === value);
    setStampVendorPhone(vendor?.phone ?? "");
    setFieldErrors((prev) => ({
      ...prev,
      stampVendorName: undefined,
      stampVendorPhone: undefined,
    }));
  };

  const vendorSelectValue =
    stampVendorName && stampVendors.some((v) => v.name === stampVendorName)
      ? stampVendorName
      : "__none__";

  const sendableMessages = useMemo(
    () => messages.filter((m) => m.stampCount > 0 && !!m.message),
    [messages]
  );

  const validate = (): { ok: boolean; firstError?: string } => {
    const errors: FieldErrors = {};
    if (!assignee.trim()) errors.assignee = "Enter the assignee name";
    if (!assigneeAddress.trim())
      errors.assigneeAddress = "Enter the assignee address";
    if (!assigneeType) errors.assigneeType = "Select the assignee type";
    if (!assigneeSignatoryName.trim())
      errors.assigneeSignatoryName = "Enter the assignee signatory name";
    if (!assigneeSignatoryDesignation.trim())
      errors.assigneeSignatoryDesignation =
        "Enter the assignee signatory designation";
    if (!stampVendorName.trim())
      errors.stampVendorName = "Enter the stamp vendor name";
    const phoneError = validateVendorPhone(stampVendorPhone);
    if (phoneError) errors.stampVendorPhone = phoneError;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors).find(
        (e): e is string => typeof e === "string"
      );
      return { ok: false, firstError };
    }
    setFieldErrors({});
    return { ok: true };
  };

  const persistAndRefresh = async (currentLeadId: string) => {
    const data = await saveAssignmentStampPaper(currentLeadId, buildPayload());
    applyPreview(data);
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validation = validate();
    if (!validation.ok) return;
    if (
      applicantPoaStamps === 0 &&
      assigneePoaStamps === 0 &&
      assignmentDeedStamps === 0 &&
      otherDocumentsStamps === 0
    ) {
      toast.error("Set at least one stamp paper count above 0.");
      return;
    }
    setSaving(true);
    try {
      await persistAndRefresh(leadId);
      if (hasSentWhatsapp) {
        toast.success("Stamp paper details to vendor saved successfully.");
        onSuccess?.(leadId);
      } else {
        setConfirmMode("send");
        setShowConfirmModal(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendAllMessages = async (currentLeadId: string) => {
    const types = sendableMessages.map((m) => m.type);
    if (types.length === 0) {
      throw new Error("No messages with positive stamp counts to send.");
    }
    for (const type of types) {
      await sendAssignmentStampPaperWhatsApp(
        currentLeadId,
        type as AssignmentStampPaperMessageType
      );
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    (async () => {
      try {
        await sendAllMessages(leadId);
        setHasSentWhatsapp(true);
        onSentStatusChange?.(true);
        toast.success(
          confirmMode === "resend"
            ? "Stamp paper details resent to vendor via WhatsApp."
            : "Stamp paper details sent to vendor successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || resending || saving) return;
    const validation = validate();
    if (!validation.ok) return;
    setResending(true);
    try {
      await persistAndRefresh(leadId);
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading stamp paper details...</p>
      </div>
    );
  }

  const previewBlocks = sendableMessages.map(
    (m) => `*${m.label}*\n\n${m.message ?? "—"}`
  );

  return (
    <form
      id={ASSIGNMENT_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <h3 className="text-sm font-medium text-[#222222]">
        Stamp Paper details to Vendor
      </h3>

      {/* ─────────────────────────────────────────────────────────────────
          Section 1 — Fetched application details (read-only)
          Sourced from Stage 1 ▸ Application Details from Client.
          ─────────────────────────────────────────────────────────────── */}
      <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
        Application Details (from previous step)
      </h4>

      <FetchedDetailsPanel
        rows={[
          { label: "Applicant Type", value: formatApplicantTypeLabel(applicantType) },
          { label: "Signatory's Designation", value: signatoryDesignation },
          { label: "Applicant", value: applicant },
          { label: "Applicant Address", value: applicantAddress },
          { label: "Signatory's Name", value: signatoryName },
          { label: "Application Number (TM No.)", value: tmNumbers },
        ]}
      />

      <div className="border-t border-[#EEE] my-1" />

      {/* ─────────────────────────────────────────────────────────────────
          Section 2 — Editable: assignee block, stamp counts, vendor.
          ─────────────────────────────────────────────────────────────── */}
      <h4 className="text-xs font-medium leading-5 text-[#222222]">
        Assignee & Stamp Vendor Details
      </h4>

      {/* Assignee | Assignee Address */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Assignee"
          value={assignee}
          onChange={(v) => {
            setAssignee(v);
            setFieldErrors((prev) => ({ ...prev, assignee: undefined }));
          }}
          placeholder="Text Here"
          errorText={fieldErrors.assignee}
        />
        <TextField
          label="Assignee Address"
          value={assigneeAddress}
          onChange={(v) => {
            setAssigneeAddress(v);
            setFieldErrors((prev) => ({
              ...prev,
              assigneeAddress: undefined,
            }));
          }}
          placeholder="Text Here"
          errorText={fieldErrors.assigneeAddress}
        />
      </div>

      {/* Assignee Type | Assignee Signatory's Name */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <ApplicantTypeSelect
          label="Assignee Type"
          value={assigneeType}
          onChange={(v) => {
            setAssigneeType(v);
            setFieldErrors((prev) => ({ ...prev, assigneeType: undefined }));
          }}
          errorText={fieldErrors.assigneeType}
        />
        <TextField
          label="Assignee Signatory's Name"
          value={assigneeSignatoryName}
          onChange={(v) => {
            setAssigneeSignatoryName(v);
            setFieldErrors((prev) => ({
              ...prev,
              assigneeSignatoryName: undefined,
            }));
          }}
          placeholder="Text Here"
          errorText={fieldErrors.assigneeSignatoryName}
        />
      </div>

      {/* Assignee Signatory's Designation | Number of Stamp Papers for PoA */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Assignee Signatory's Designation"
          value={assigneeSignatoryDesignation}
          onChange={(v) => {
            setAssigneeSignatoryDesignation(v);
            setFieldErrors((prev) => ({
              ...prev,
              assigneeSignatoryDesignation: undefined,
            }));
          }}
          placeholder="Text Here"
          errorText={fieldErrors.assigneeSignatoryDesignation}
        />
        <StampCountSelect
          label="Number of Stamp Papers for PoA"
          value={applicantPoaStamps}
          onChange={(v) => setApplicantPoaStamps(v)}
        />
      </div>

      {/* Number of Stamp Papers for Assignee PoA | Number of Stamp Papers for Assignment Deed */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <StampCountSelect
          label="Number of Stamp Papers for Assignee PoA"
          value={assigneePoaStamps}
          onChange={(v) => setAssigneePoaStamps(v)}
        />
        <StampCountSelect
          label="Number of Stamp Papers for Assignment Deed"
          value={assignmentDeedStamps}
          onChange={(v) => setAssignmentDeedStamps(v)}
        />
      </div>

      {/* Number of Stamp Papers for Other Documents | Name of Stamp Vendor (quick-select) */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <StampCountSelect
          label="Number of Stamp Papers for Other Documents"
          value={otherDocumentsStamps}
          onChange={(v) => setOtherDocumentsStamps(v)}
        />
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Name of Stamp Vendor
          </label>
          <Select value={vendorSelectValue} onValueChange={handleVendorSelect}>
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select to autofill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Add new vendor —</SelectItem>
              {stampVendors
                .filter(
                  (v) => typeof v.name === "string" && v.name.trim().length > 0
                )
                .map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name}
                    {v.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Editable Name + Phone — always visible, autofilled by the dropdown,
          but the user can edit them or enter a brand-new vendor. */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Name of Stamp Vendor"
          value={stampVendorName}
          onChange={(v) => {
            setStampVendorName(v);
            setFieldErrors((prev) => ({
              ...prev,
              stampVendorName: undefined,
            }));
          }}
          placeholder="Vendor name"
          errorText={fieldErrors.stampVendorName}
        />
        <TextField
          label="Phone of Stamp Vendor"
          value={stampVendorPhone}
          onChange={(v) => {
            setStampVendorPhone(v);
            setFieldErrors((prev) => ({
              ...prev,
              stampVendorPhone: undefined,
            }));
          }}
          placeholder="Phone number"
          type="tel"
          inputMode="tel"
          errorText={fieldErrors.stampVendorPhone}
        />
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewBlocks.length > 0 ? previewBlocks : "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={previewBlocks.length === 0}
      />

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            disabled={resending || saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}
